import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  A11y,
  Controller,
  EffectFade,
  Mousewheel,
  Navigation,
  Pagination,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useRouter } from 'next/router';
import cls from 'classnames';

import { getContent } from '@/utils/helper';
import { analytics, getClickCtaDetail } from '@/utils/analytics';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';
import Pill from '@/atoms/Pill/Pill';
import CtfCta, { Cta, CtaWrapper } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import RichText from '@/molecules/RichText/RichText';

import { CtfCtaStyle } from '@/utils/constants';
import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';

const SWIPER_BREAKPOINTS = {
  360: {
    autoHeight: true,
  },
  640: {
    autoHeight: false,
  },
};

const SWIPER_MODULES = [A11y, Controller, Mousewheel, Navigation, Pagination];

const SWIPER_NAVIGATION = {
  nextEl: '.aaco-carousel-normal__next',
  prevEl: '.aaco-carousel-normal__prev',
};

const SWIPER_PAGINATION = {
  el: '.aaco-carousel-normal__pagination',
  type: 'custom',
  renderCustom: function (_swiper, current, total) {
    const formattedCurrent = current < 10 ? `0${current}` : current;
    const formattedTotal = total < 10 ? `0${total}` : total;
    return `${formattedCurrent}/${formattedTotal}`;
  },
};

const SWIPER_SPEED = 1000;

// @TODO: Update left and right arrow carousel nav with proper icon < >
function CarouselNormal({
  assets,
  carouselContentStyle,
  hasBackground,
  entries,
  fields,
}) {
  const router = useRouter();

  const [firstSwiper, setFirstSwiper] = useState(null);
  const [secondSwiper, setSecondSwiper] = useState(null);
  const [thirdSwiper, setThirdSwiper] = useState(null);

  const {
    callToAction,
    cards,
    contentTags = [],
    featuredCardTitle = false,
    title = '',
    removeYPadding,
    id,
    subTitle,
    // subTitlePadding = false,
    locationText,
    superText,
    backgroundColor,
    hideTitle,
    titleSize,
  } = fields;

  const tag =
    contentTags.length > 0 ? getContent(contentTags[0].sys?.id, entries) : null;

  return (
    <div
      id={id}
      className={`aaco-carousel-normal overflow-hidden bg-${backgroundColor}`}
      aria-label={title}
    >
      <div
        className={`${
          !carouselContentStyle?.compactSpace && `container mx-auto px-6 py-9 md:p-10 xl:px-16`
        }`}
      >
        <div className="flex flex-col md:flex-row justify-between gap-y-4 md:gap-y-0 gap-x-[60px]">
          <div
            className={`content z-20 relative ${
              !carouselContentStyle?.compactSpace &&
              'w-[380px] flex-shrink-0'
            }
            `}
          >
            {featuredCardTitle ? (
              <Swiper
                breakpoints={SWIPER_BREAKPOINTS}
                direction="horizontal"
                effect="fade"
                enabled={false}
                fadeEffect={{ crossFade: true }}
                modules={[...SWIPER_MODULES, EffectFade]}
                mousewheel={{
                  forceToAxis: true,
                }}
                onSwiper={setThirdSwiper}
                speed={SWIPER_SPEED}
              >
                {cards.map((card, index) => {
                  const content = getContent(card.sys?.id, entries);
                  const { primaryCTA, tags = [], text, title } = content.fields;
                  const tag =
                    tags.length > 0
                      ? getContent(tags[0].sys?.id, entries)
                      : null;
                  return (
                    <SwiperSlide key={index}>
                      <div className="content relative">
                        {tag && (
                          <Pill
                            style="pill-action mb-4 inline-flex items-center"
                            onClick={() =>
                              router.push(`/search?t=${tag?.fields?.slug}`)
                            }
                            small
                          >
                            {tag.fields?.name}
                          </Pill>
                        )}
                        <h2 className="text-h4 mb-5 font-heading">{title}</h2>
                        {text && (
                          <p className="text-grey-6 text-body">{text}</p>
                        )}
                        {primaryCTA && (
                          <CtfCta field={primaryCTA} entries={entries} />
                        )}
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            ) : (
              <div className={`flex flex-col ${locationText ? "justify-between" : "justify-start"} h-full max-h-[38rem]`}>
                <div className="block">
                  {tag && (
                    <Pill
                      style="pill-action mb-4 inline-flex items-center"
                      onClick={() =>
                        router.push(`/search?t=${tag?.fields?.slug}`)
                      }
                      small
                    >
                      {tag.fields?.name}
                    </Pill>
                  )}
                  {superText && (
                    <Text
                      tag="tag"
                      style="text-black font-pro-mono"
                    >
                      {superText}
                    </Text>
                  )}
                  <Text tag={subTitle && !titleSize ? "h3" : titleSize ? titleSize : "h4"} style={`text-black mb-4 ${hideTitle ? "opacity-0" : ""}`}>{title}</Text>
                  {subTitle && (
                    <hr className={`border-black my-4 md:my-8 ${hideTitle ? "opacity-0" : ""}`} />
                  )}
                </div>
                <div className={`flex flex-col gap-4 justify-end`}>
                  {locationText && (
                    <div className="text-location-carousel-normal">
                      <Text
                        tag="tag"
                        style="text-transparent border-l-1 border-l-transparent pl-4 font-pro-mono"
                      >
                        {locationText}
                      </Text>
                    </div>
                  )}
                  <div className="text-content-carousel-normal grid gap-y-4">
                    {subTitle &&
                      subTitle.content && subTitle.content.map(({ content, nodeType }, idx) => {
                      return (
                        <RichText
                          entries={entries}
                          content={content}
                          key={idx}
                          nodeType={nodeType}
                          richTextStyle={{
                            paragraph: TEXT_BASE_CLASS['body'] + ' text-black',
                          }}
                        />
                      );
                    })}
                    {callToAction && (
                      <CtfCta
                        field={callToAction}
                        entries={entries}
                        onClick={(e, { localUrl, url, name }) =>
                          analytics({
                            window,
                            document,
                            detail: getClickCtaDetail(e, { localUrl, url, name }),
                          })
                        }
                      />
                    )}
                  </div>

                </div>
              </div>
            )}
          </div>
          <div className="flex-1 max-w-[1010px]">
            {cards && (
              <div className="aaco-carousel-card relative">
                <Swiper
                  className="aaco-swiper"
                  controller={{ control: [secondSwiper, thirdSwiper] }}
                  modules={SWIPER_MODULES}
                  onSwiper={setFirstSwiper}
                  spaceBetween={60}
                  speed={SWIPER_SPEED}
                  slidesPerView={'auto'}
                >
                  {cards.map((card, index) => {
                    const content = getContent(card.sys?.id, entries);
                    const {
                      dynamicAsset,
                      cloudinaryAssets,
                      primaryCTA,
                      secondaryCTA,
                      previewAsset,
                      title,
                    } = content.fields;
                    const asset =
                      cloudinaryAssets ?? dynamicAsset ?? previewAsset;
                    return (
                      <SwiperSlide className="w-[330px] h-[415px]" key={index}>
                        <div
                          className="aaco-carousel-card__slide w-full h-fit"
                        >
                          {asset && (
                            <div className="aaco-card__image w-full">
                              <DynamicAsset
                                assets={assets}
                                entries={entries}
                                field={asset}
                                container="portrait"
                                title={title}
                              />
                            </div>
                          )}
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
                <Swiper
                  className="mt-[20px] mx-0 max-w-[330px] h-[160px]"
                  controller={{ control: [firstSwiper] }}
                  effect="fade"
                  fadeEffect={{ crossFade: true }}
                  modules={[...SWIPER_MODULES, EffectFade]}
                  navigation={SWIPER_NAVIGATION}
                  onSwiper={setSecondSwiper}
                  pagination={SWIPER_PAGINATION}
                  speed={SWIPER_SPEED}
                >
                  {cards.map((card, index) => {
                    const content = getContent(card.sys?.id, entries);
                    let { primaryCTA, text, subRichText, title } = content?.fields;
                    text =
                      text && text.sys
                        ? getContent(text.sys.id, entries)?.fields.text
                        : text;
                    subRichText =
                      subRichText && subRichText.sys
                        ? getContent(subRichText.sys.id, entries)?.fields.subRichText
                        : subRichText;

                    return (
                      <SwiperSlide key={index} className='h-[160px]'>
                        <div
                          className={`content relative px-5 ${
                            featuredCardTitle ? 'hidden' : ''
                          } ${
                            !carouselContentStyle?.compactSpace && 'md:px-4'
                          } border-black border-t-1 border-b-1 py-4 !px-0`}
                        >
                          {title && (
                            <>
                              {card?.fields?.slug ? (
                                <CtaWrapper
                                  page={card?.fields?.slug}
                                  classNames={carouselContentStyle?.linkStyle}
                                >
                                  <Text tag={'h7'} style="aaco-card__title uppercase mt-2">
                                    {title}
                                  </Text>
                                </CtaWrapper>
                              ) : (
                              <Text tag="body" style="uppercase">
                                {title}
                              </Text>
                            )}
                            </>
                          )}
                          {text && !text.content && (
                            <p className="text-body text-black uppercase">{text}</p>
                          )}
                          {subRichText &&
                            subRichText.content &&
                            subRichText.content.length > 0 &&
                            subRichText.content.map(
                              ({ nodeType, content }, index) => (
                                <RichText
                                  entries={entries}
                                  nodeType={nodeType}
                                  content={content}
                                  key={index}
                                  richTextStyle={{
                                    paragraph: {
                                      customFont:
                                        TEXT_BASE_CLASS['body'] +
                                        ' uppercase !font-pro-mono text-[13.5px]'
                                    },
                                  }}
                                />
                              )
                            )}
                          {primaryCTA && primaryCTA.sys && (
                            <div className="aaco-card__cta mt-2">
                              <CtfCta field={primaryCTA} entries={entries} />
                            </div>
                          )}
                          {primaryCTA &&
                            !primaryCTA.sys &&
                            !carouselContentStyle.compactSpace && (
                              <div className="aaco-carousel-hero__cta my-2">
                                <Cta
                                  buttonProps={{
                                    size:"small",
                                    invert: false,
                                    name:
                                      router?.locale === 'ko-KR'
                                        ? '자세히 보기'
                                        : 'Learn more',
                                    type: CtfCtaStyle.PRIMARY,
                                    style: "w-fit text-black text-button",
                                  }}
                                  iconProps={{
                                    name: 'arrow-right',
                                    height: 15,
                                    width: 15,
                                    style:
                                      'text-acacia',
                                    hasIconBorder: true,
                                  }}
                                  linkProps={{
                                    url: primaryCTA,
                                  }}
                                />
                              </div>
                            )}
                        </div>
                      </SwiperSlide>
                    );
                  })}
                  <div
                    className={`swiper-control flex items-center justify-start ${
                      featuredCardTitle
                        ? `py-1 px-5 ${
                            !carouselContentStyle?.compactSpace && 'md:px-10'
                          }`
                        : `absolute bottom-4 z-20`
                    }`}
                  >
                    <div className="aaco-carousel-normal__prev">
                      <Icon
                        name="chevron-left"
                        height={14}
                        width={14}
                        className="text-black"
                      />
                    </div>
                    <div
                      className="flex-grow-0 w-auto mx-4 text-dark-warm-grey font-body text-body"
                    >
                      <div className="aaco-carousel-normal__pagination"></div>
                    </div>
                    <div className="aaco-carousel-normal__next">
                      <Icon
                        name="chevron-right"
                        className="text-black"
                        height={14}
                        width={14}
                      />
                    </div>
                  </div>
                </Swiper>
              </div>
            )}
          </div>
        </div>
        <hr className="border-black hidden md:block relative" />
      </div>
    </div>
  );
}

CarouselNormal.propTypes = {
  assets: PropTypes.array,
  carouselContentStyle: PropTypes.object,
  entries: PropTypes.array,
  fields: PropTypes.object,
};

CarouselNormal.defaultProps = {
  assets: [],
  carouselContentStyle: {
    compactSpace: false,
    hasBackground: false,
    linkStyle: '',
  },
  entries: [],
  fields: null,
};

export default CarouselNormal;
